import React, { useState } from 'react';
import { View, StyleSheet, Alert, ActivityIndicator, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { AuthStackParamList } from '../../navigation/AuthNavigator';
import { auth, sendPasswordResetEmail } from '../../config/firebase';
import { showAlert } from '../../utils/alert';
import { 
  PageContainer, 
  ContentContainer, 
  Text, 
  Button, 
  Input, 
  Theme 
} from '../../components/ui';

type ForgotPasswordScreenNavigationProp = NativeStackNavigationProp<AuthStackParamList, 'ForgotPassword'>;

const ForgotPasswordScreen = () => {
  const navigation = useNavigation<ForgotPasswordScreenNavigationProp>();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [hasInteractedWithEmail, setHasInteractedWithEmail] = useState(false);

  const validateEmail = (email: string) => {
    if (!email.trim()) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const handleEmailChange = (text: string) => {
    setEmail(text);
    if (hasInteractedWithEmail) {
      setEmailError(validateEmail(text));
    }
  };

  const handleEmailBlur = () => {
    setHasInteractedWithEmail(true);
    setEmailError(validateEmail(email));
  };

  const isFormValid = () => {
    return validateEmail(email) === '';
  };

  const handleSendResetLink = async () => {
    // Validate email and show error
    const emailValidation = validateEmail(email);
    setEmailError(emailValidation);
    setHasInteractedWithEmail(true);
    
    if (!isFormValid()) {
      return;
    }

    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      showAlert('Check Your Email', 'A password reset link has been sent to your email address.');
      navigation.navigate('Login');
    } catch (error: any) {
      console.error('Password reset error:', error);
      let errorMessage = 'An unknown error occurred.';
      if (error.code === 'auth/invalid-email') {
        errorMessage = 'That email address is invalid!';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'There is no user record corresponding to this email.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      showAlert('Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const navigateToLogin = () => {
    navigation.navigate('Login');
  };

  return (
    <PageContainer>
      <ContentContainer>
        <View style={styles.content}>
          <Text variant="heading1" align="center" style={styles.title}>
            Reset Password
          </Text>
          
          <Text variant="body1" color="secondary" align="center" style={styles.subtitle}>
            Enter your email address and we'll send you a link to reset your password.
          </Text>
          
          <View style={styles.formContainer}>
            <Input
              placeholder="Email"
              value={email}
              onChangeText={handleEmailChange}
              onBlur={handleEmailBlur}
              keyboardType="email-address"
              autoCapitalize="none"
              editable={!loading}
              error={!!emailError}
              errorMessage={emailError}
            />
            
            {loading ? (
              <ActivityIndicator 
                size="large" 
                color={Theme.colors.primary} 
                style={styles.loader}
              />
            ) : (
              <Button 
                title="Send Reset Link" 
                onPress={handleSendResetLink}
                style={styles.resetButton}
                disabled={loading || (hasInteractedWithEmail && !isFormValid())}
              />
            )}
          </View>

          <View style={styles.loginSection}>
            <Button
              title="Back to Sign In"
              variant="link"
              onPress={navigateToLogin}
              disabled={loading}
              style={styles.linkButton}
            />
          </View>
        </View>
      </ContentContainer>
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: Theme.spacing.xxl,
  },
  title: {
    marginBottom: Theme.spacing.xl,
    fontFamily: Theme.typography.fontFamily.semiBold,
  },
  subtitle: {
    marginBottom: Theme.spacing.xxxxl,
    paddingHorizontal: Theme.spacing.md,
    lineHeight: 24,
  },
  formContainer: {
    gap: Theme.spacing.lg,
    marginBottom: Theme.spacing.xxxxl,
  },
  resetButton: {
    marginTop: Theme.spacing.md,
  },
  loginSection: {
    alignItems: 'center',
  },
  linkButton: {
    alignSelf: 'center',
  },
  loader: {
    marginVertical: Theme.spacing.xl,
  },
});

export default ForgotPasswordScreen; 